import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Download, 
  CheckCircle, 
  Clock, 
  Calendar,
  MapPin,
  Search,
  Filter,
  User,
  Plus,
  Edit,
  ChevronDown,
  ChevronUp,
  Building2,
  Info
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import ManualTrainingRegistration from '../components/ManualTrainingRegistration';
import DisciplineBoxes from '../components/DisciplineBoxes';
import SkytedagbokModal from '../components/SkytedagbokModal';
import { useToast } from '../components/Toast';
import MobileBookmarkBanner from '../components/MobileBookmarkBanner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const TrainingLog = () => {
  const { user, isSuperAdmin, isOrgAdmin, isRangeOfficer } = useAuth();
  const { t, currentLanguage, changeLanguage, loadAvailableLanguages } = useLanguage();
  const toast = useToast();
  
  // Sorting function
  const handleColumnSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Training diary functions
  const openDiaryModal = (session) => {
    setSelectedSession(session);
    setShowDiaryModal(true);
  };

  const closeDiaryModal = () => {
    setShowDiaryModal(false);
    setSelectedSession(null);
  };

  const handleDiaryUpdate = () => {
    // Refresh training sessions after diary update
    loadTrainingSessions();
  };

  // Function to check if session has diary data (v1 legacy fields OR v2 Skytedagbok fields)
  const hasDiaryData = (session) => {
    if (!session) return false;
    // v1 legacy fields
    if (session.weapon_type) return true;
    if (session.result_description) return true;
    if (session.training_notes) return true;
    if (session.target_images && session.target_images.length > 0) return true;
    if (session.target_images_s3_urls && session.target_images_s3_urls.length > 0) return true;
    // v2 Skytedagbok fields (any signal counts as "has interacted")
    if (session.diary_weapon_id) return true;
    if (session.diary_ammo_id) return true;
    if (Array.isArray(session.series) && session.series.length > 0) return true;
    if (session.clicks_height_count && session.clicks_height_count > 0) return true;
    if (session.clicks_side_count && session.clicks_side_count > 0) return true;
    if (Array.isArray(session.clicks_history) && session.clicks_history.length > 0) return true;
    if (session.dagsform && session.dagsform > 0) return true;
    if (session.wind_level !== null && session.wind_level !== undefined) return true;
    if (Array.isArray(session.tags) && session.tags.length > 0) return true;
    if (Array.isArray(session.sight_tags) && session.sight_tags.length > 0) return true;
    if (session.group_size_mm && Number(session.group_size_mm) > 0) return true;
    if (session.group_size_caliber_mm && Number(session.group_size_caliber_mm) > 0) return true;
    if (session.group_size_shots && Number(session.group_size_shots) > 0) return true;
    if (session.group_size_notes) return true;
    if (session.timer_seconds && Number(session.timer_seconds) > 0) return true;
    if (session.notes) return true;
    if (Array.isArray(session.images) && session.images.length > 0) return true;
    return false;
  };

  // Function to get branch info from location discipline (PREFERRED METHOD)
  const getBranchFromLocation = (session) => {
    // Use location discipline if available (more reliable)
    if (session.location_discipline) {
      const disciplineKey = session.location_discipline.toLowerCase();
      const map = {
        nsf:  { key: 'nsf',  name: 'NSF',  color: 'bg-blue-500',   textColor: 'text-white' },
        dfs:  { key: 'dfs',  name: 'DFS',  color: 'bg-purple-500', textColor: 'text-white' },
        dssn: { key: 'dssn', name: 'DSSN', color: 'bg-green-500',  textColor: 'text-white' },
        sws:  { key: 'sws',  name: 'SWS',  color: 'bg-amber-700',  textColor: 'text-white' },
        nsu:  { key: 'nsu',  name: 'NSU',  color: 'bg-slate-600',  textColor: 'text-white' },
        nrof: { key: 'nrof', name: 'NROF', color: 'bg-lime-700',   textColor: 'text-white' },
        nmsf: { key: 'nmsf', name: 'NMSF', color: 'bg-fuchsia-700',textColor: 'text-white' },
        njff: { key: 'njff', name: 'NJFF', color: 'bg-green-700',  textColor: 'text-white' },
        nbif: { key: 'nbif', name: 'NBIF', color: 'bg-sky-600',    textColor: 'text-white' },
        nbsf: { key: 'nbsf', name: 'NBSF', color: 'bg-rose-600',   textColor: 'text-white' },
        nssf: { key: 'nssf', name: 'NSSF', color: 'bg-cyan-600',   textColor: 'text-white' },
      };
      if (map[disciplineKey]) return map[disciplineKey];
    }
    
    // Fallback to activity-based detection for older sessions
    return getActivityBranch(session.activity || session.training_type || 'Pistoltrening');
  };

  // Function to determine which branch an activity belongs to (FALLBACK METHOD)
  const getActivityBranch = (activity) => {
    if (!activity) return null;
    
    const activityLower = activity.toLowerCase();
    
    // DSSN - Dynamic shooting activities (CHECK FIRST to avoid conflicts with NSF keywords)
    if (activityLower.includes('dssn') ||
        activityLower.includes('dssn demobane') ||
        activityLower.includes('dssn-bane') ||
        activityLower.includes('dynamisk skyting') ||
        activityLower.includes('dynamiskbane') ||
        activityLower.includes('ipsc') ||
        activityLower.includes('uspsa') ||
        activityLower.includes('action') ||
        activityLower.includes('praktisk skyting') ||
        activityLower.includes('idpa')) {
      return {
        key: 'dssn',
        name: 'DSSN', 
        color: 'bg-green-500',
        textColor: 'text-white'
      };
    }
    
    // DFS - Field shooting activities  
    if (activityLower.includes('dfs') ||
        activityLower.includes('felt') ||
        activityLower.includes('dynamisk felt') ||
        activityLower.includes('field') ||
        activityLower.includes('hagle') ||
        activityLower.includes('shotgun')) {
      return {
        key: 'dfs', 
        name: 'DFS',
        color: 'bg-purple-500',
        textColor: 'text-white'
      };
    }
    
    // NSF - Traditional shooting activities
    if (activityLower.includes('nsf') || 
        activityLower.includes('pistol') ||
        activityLower.includes('rifle') ||
        activityLower.includes('precisjon') ||
        activityLower.includes('tradisjonell') ||
        activityLower.includes('skytebane') ||
        activityLower.includes('grunnleggende')) {
      return {
        key: 'nsf',
        name: 'NSF', 
        color: 'bg-blue-500',
        textColor: 'text-white'
      };
    }
    
    // Default - NSF for unknown activities (should be blue like other NSF)
    return {
      key: 'nsf',
      name: 'NSF',
      color: 'bg-blue-500',
      textColor: 'text-white'
    };
  };
  
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all, verified, pending
  const [exportLoading, setExportLoading] = useState(false);
  const [showManualRegistration, setShowManualRegistration] = useState(false);
  const [disciplineRefresh, setDisciplineRefresh] = useState(0);
  const [showPdfOptions, setShowPdfOptions] = useState(false);
  const [organizationSettings, setOrganizationSettings] = useState(null);
  
  // State for selected documents to include in PDF
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  
  // MULTI-CLUB: cross-club PDF support
  const [includeAllClubs, setIncludeAllClubs] = useState(true); // Default ON for multi-club users
  const [pdfMemberships, setPdfMemberships] = useState([]);
  const [pdfMultiClubEnabled, setPdfMultiClubEnabled] = useState(false);
  
  // Organization stats for admin/range officer view
  const [orgStats, setOrgStats] = useState({
    totalMembers: 0,
    totalSessions: 0,
    verifiedSessions: 0,
    pendingSessions: 0
  });
  
  // ADVANCED FILTERING STATES
  const [dateFilter, setDateFilter] = useState('alle'); // 'alle', '6months', '1year', '2years'
  const [activityTypes, setActivityTypes] = useState(new Set()); // Set of selected activity types
  const [locationFilter, setLocationFilter] = useState('');
  const [rangeOfficerFilter, setRangeOfficerFilter] = useState('');
  const [selectedBranches, setSelectedBranches] = useState(new Set()); // Set of selected branches (multi-select)
  const [organizationFilter, setOrganizationFilter] = useState(0); // Index of selected organization from memberOrganizations
  const [diaryFilter, setDiaryFilter] = useState('alle'); // 'alle', 'med_dagbok', 'uten_dagbok'
  const [showFilters, setShowFilters] = useState(false);

  // Sorting states
  const [sortColumn, setSortColumn] = useState('start_time');
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'

  // Training diary modal states
  const [showDiaryModal, setShowDiaryModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  
  // Mobile responsiveness states
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // Bulk delete states
  const [selectedSessions, setSelectedSessions] = useState(new Set());
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Responsive table expand: on mobile show 3, tablet 5, desktop 10 (full page).
  // "Vis mer" button expands to show all rows for the current page.
  const [tableExpanded, setTableExpanded] = useState(false);
  const [screenSize, setScreenSize] = useState(() => {
    if (typeof window === 'undefined') return 'desktop';
    const w = window.innerWidth;
    return w < 768 ? 'mobile' : w < 1024 ? 'tablet' : 'desktop';
  });
  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      setScreenSize(w < 768 ? 'mobile' : w < 1024 ? 'tablet' : 'desktop');
    };
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  const initialVisibleRows = screenSize === 'mobile' ? 3 : screenSize === 'tablet' ? 5 : 10;
  
  // NEW: State for organization activity types from settings
  const [organizationActivityTypes, setOrganizationActivityTypes] = useState([]);
  
  // Get unique activity types for checkboxes - CHANGED to use organization settings
  const uniqueActivityTypes = organizationActivityTypes.length > 0 
    ? organizationActivityTypes 
    : [...new Set(sessions.map(s => s.activity).filter(Boolean))];
  
  // Helper function to check if date is within filter range
  const isDateInRange = (sessionDate, filterType) => {
    if (filterType === 'alle') return true;
    
    const date = new Date(sessionDate);
    const now = new Date();
    const monthsBack = {
      '6months': 6,
      '1year': 12,
      '2years': 24
    };
    
    const cutoffDate = new Date(now.getFullYear(), now.getMonth() - monthsBack[filterType], now.getDate());
    return date >= cutoffDate;
  };
  
  console.log("🔄 TrainingLog component rendered, showPdfOptions:", showPdfOptions);

  // Check if DSSN is active in organization shooting branches
  // shooting_branches is an object, not an array
  const isDssnActive = user?.organization?.shooting_branches?.dssn === true || false;
  console.log("📊 DSSN active:", isDssnActive, "shooting_branches:", user?.organization?.shooting_branches);

  useEffect(() => {
    if (user?.organization_id) {
      // NOTE: loadTrainingSessions is now called by separate useEffect that waits for multiClubLoaded
      // to avoid race conditions between single-club and cross-club requests
      loadOrganizationStats(); // Load org stats for admin/skyteleder
      // Trigger discipline boxes refresh when component mounts
      setDisciplineRefresh(prev => prev + 1);
      // Load available languages
      loadAvailableLanguages(user.organization_id, user.role);
      
      // NEW: Load organization activity types from user.organization.settings
      if (user?.organization?.settings?.activity_types) {
        const activityTypes = user.organization.settings.activity_types;
        setOrganizationActivityTypes(activityTypes);
        console.log('✅ Loaded organization activity types from user.organization:', activityTypes);
      } else if (user?.organization) {
        // Fallback: Try to get activity_types directly from organization
        const activityTypes = user.organization.activity_types || [];
        if (activityTypes.length > 0) {
          setOrganizationActivityTypes(activityTypes);
          console.log('✅ Loaded organization activity types (fallback):', activityTypes);
        }
      }
    }
  }, [user]);

  useEffect(() => {
    // Ensure search field starts empty (clear any cached values)
    setSearch('');
    setRangeOfficerFilter('');
    setLocationFilter('');
    
    // Clear any potential localStorage or session storage that might be caching search values
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('trainingLogSearch');
        localStorage.removeItem('searchValue');
      }
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('trainingLogSearch');
        sessionStorage.removeItem('searchValue');
      }
    } catch (e) {
      console.log('Could not clear storage:', e);
    }
  }, []);

  // Pre-select all documents when PDF options dialog opens
  useEffect(() => {
    if (showPdfOptions && user) {
      // Build available documents list (same logic as in render)
      const availableDocs = [];
      
      // Helper to build correct doc URL (matches the logic used in UI render below)
      const buildDocUrl = (rawUrl) => {
        if (!rawUrl || typeof rawUrl !== 'string') return null;
        if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
          return rawUrl;
        }
        if (rawUrl.startsWith('/api')) {
          // Local file with /api path – use ingress routing for proxy compatibility
          return `${window.location.origin}${rawUrl}`;
        }
        // Local file without /api path
        return `${API_BASE}${rawUrl}`;
      };
      
      if (user.startkort_url) {
        const fullUrl = buildDocUrl(user.startkort_url);
        if (fullUrl) availableDocs.push(fullUrl);
      }
      
      if (user.medlemskort_urls?.length > 0) {
        user.medlemskort_urls.forEach((url) => {
          const fullUrl = buildDocUrl(url);
          if (fullUrl) availableDocs.push(fullUrl);
        });
      }
      
      if (user.weapon_cert_urls?.length > 0) {
        user.weapon_cert_urls.forEach((url) => {
          const fullUrl = buildDocUrl(url);
          if (fullUrl) availableDocs.push(fullUrl);
        });
      }
      
      // Pre-select all documents (default to all selected)
      if (availableDocs.length > 0) {
        setSelectedDocuments(availableDocs);
        console.log('📄 Pre-selected all documents for PDF:', availableDocs.length);
      }
      
      // MULTI-CLUB: Fetch memberships + feature flag status
      (async () => {
        try {
          const flagsRes = await axios.get(`${API_BASE}/api/feature-flags`);
          const enabled = !!flagsRes.data?.multi_club_enabled;
          setPdfMultiClubEnabled(enabled);
          if (enabled) {
            const memRes = await axios.get(`${API_BASE}/api/auth/memberships`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            setPdfMemberships(memRes.data?.memberships || []);
          }
        } catch (e) {
          setPdfMultiClubEnabled(false);
        }
      })();
    }
  }, [showPdfOptions, user]);

  // Refresh discipline boxes when page becomes visible (user returns from settings)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('TrainingLog page visible, refreshing discipline boxes');
        setDisciplineRefresh(prev => prev + 1);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const loadTrainingSessions = async () => {
    try {
      // Always load user's own sessions for "Min logg" section
      const params = {
        user_id: user.id
      };
      // MULTI-CLUB: opt-in to fetch sessions from all linked clubs
      if (multiClubEnabled && memberClubMemberships.length > 1) {
        params.include_all_my_clubs = true;
        delete params.user_id; // Backend uses identity_id when include_all_my_clubs=true
      }
      // Apply diary filters if set
      if (diaryFilters.weapon_id) params.diary_weapon_id = diaryFilters.weapon_id;
      if (diaryFilters.ammo_id) params.diary_ammo_id = diaryFilters.ammo_id;
      if (diaryFilters.tag) params.tag = diaryFilters.tag;
      if (diaryFilters.year) {
        params.date_from = `${diaryFilters.year}-01-01`;
        params.date_to = `${diaryFilters.year}-12-31`;
      }
      // MULTI-CLUB: filter by specific clubs (multi-select)
      // If 1 club selected -> send organization_id to backend (fast filter)
      // If 0 or all clubs -> let frontend handle (fetch all)
      if (diaryFilters.club_ids && diaryFilters.club_ids.length === 1) {
        params.organization_id = diaryFilters.club_ids[0];
      }

      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.get(`${API_BASE}/api/training/sessions`, { params, headers });
      setSessions(response.data);
    } catch (error) {
      console.error('Failed to load training sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Diary filter data + state
  // club_ids is now an array of selected org IDs (multi-select like Skyttergren)
  const [diaryFilters, setDiaryFilters] = useState({ weapon_id: '', ammo_id: '', tag: '', year: '', club_ids: [] });
  const [diaryWeapons, setDiaryWeapons] = useState([]);
  const [diaryAmmo, setDiaryAmmo] = useState([]);
  const [diaryTags, setDiaryTags] = useState([]);
  const [showDiaryFilters, setShowDiaryFilters] = useState(false);
  
  // MULTI-CLUB state for Min Logg
  const [memberClubMemberships, setMemberClubMemberships] = useState([]);
  const [multiClubEnabled, setMultiClubEnabled] = useState(false);
  const [multiClubLoaded, setMultiClubLoaded] = useState(false);

  useEffect(() => {
    const loadDiaryFilterData = async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const [w, a, t] = await Promise.all([
          axios.get(`${API_BASE}/api/diary/weapons`, { headers }),
          axios.get(`${API_BASE}/api/diary/ammo`, { headers }),
          axios.get(`${API_BASE}/api/diary/tags`, { headers }),
        ]);
        setDiaryWeapons(w.data || []);
        setDiaryAmmo(a.data || []);
        setDiaryTags(t.data || []);
      } catch (err) {
        console.error('Failed to load diary filter data:', err);
      }
    };
    if (user?.id) loadDiaryFilterData();
  }, [user?.id]);
  
  // MULTI-CLUB: Load feature flag + member's club memberships
  useEffect(() => {
    const loadMemberClubs = async () => {
      try {
        const flagsRes = await axios.get(`${API_BASE}/api/feature-flags`);
        const enabled = !!flagsRes.data?.multi_club_enabled;
        setMultiClubEnabled(enabled);
        if (!enabled || user?.role === 'super_admin') {
          setMultiClubLoaded(true);
          return;
        }
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const memRes = await axios.get(`${API_BASE}/api/auth/memberships`, { headers });
        setMemberClubMemberships(memRes.data?.memberships || []);
      } catch (err) {
        // ignore
      } finally {
        setMultiClubLoaded(true);
      }
    };
    if (user?.email) loadMemberClubs();
  }, [user?.email, user?.role]);

  // Re-fetch sessions when filters change OR memberships finish loading
  // Wait until multiClubLoaded=true to avoid race condition between
  // single-club and cross-club requests overwriting each other.
  useEffect(() => {
    if (user?.id && multiClubLoaded) {
      loadTrainingSessions();
      setCurrentPage(1); // Reset to first page on filter change
    }
    // eslint-disable-next-line
  }, [diaryFilters, multiClubLoaded]);

  const clearDiaryFilters = () =>
    setDiaryFilters({ weapon_id: '', ammo_id: '', tag: '', year: '', club_ids: [] });
  const activeFilterCount = Object.values(diaryFilters).filter((v) =>
    Array.isArray(v) ? v.length > 0 : Boolean(v)
  ).length;

  // Load organization statistics for skyteleder/admin view
  const loadOrganizationStats = async () => {
    if (!isOrgAdmin && !isRangeOfficer && !isSuperAdmin) {
      return; // Only load for admin roles
    }

    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      
      // Get organization sessions
      const params = isSuperAdmin ? {} : { organization_id: user.organization_id };
      const sessionsResponse = await axios.get(`${API_BASE}/api/training/sessions`, { params, headers });
      const orgSessions = sessionsResponse.data;
      
      // Get organization members count
      const usersResponse = await axios.get(`${API_BASE}/api/users`, { headers });
      const orgUsers = isSuperAdmin 
        ? usersResponse.data 
        : usersResponse.data.filter(u => u.organization_id === user.organization_id);
      
      setOrgStats({
        totalMembers: orgUsers.length,
        totalSessions: orgSessions.length,
        verifiedSessions: orgSessions.filter(s => s.verified).length,
        pendingSessions: orgSessions.filter(s => !s.verified).length
      });
    } catch (error) {
      console.error('Failed to load organization stats:', error);
    }
  };

  // Debounce timer for competition number auto-save
  const [competitionNumberTimers, setCompetitionNumberTimers] = useState({});

  // Handle competition number change with 5 second debounce
  const handleCompetitionNumberChange = (sessionId, value) => {
    // Update local state immediately for responsive UI
    setSessions(prevSessions =>
      prevSessions.map(s =>
        s.id === sessionId ? { ...s, competition_number: value } : s
      )
    );

    // Clear existing timer for this session
    if (competitionNumberTimers[sessionId]) {
      clearTimeout(competitionNumberTimers[sessionId]);
    }

    // Set new timer for auto-save after 5 seconds
    const newTimer = setTimeout(async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        await axios.patch(
          `${API_BASE}/api/training/sessions/${sessionId}`,
          { competition_number: value },
          { headers }
        );
        console.log(`✅ Competition number saved for session ${sessionId}: "${value}"`);
      } catch (error) {
        console.error('Failed to save competition number:', error);
        toast.error('Kunne ikke lagre stevne-nr');
        // Reload to get correct state
        loadTrainingSessions();
      }
    }, 5000); // 5 seconds

    // Store the timer
    setCompetitionNumberTimers(prev => ({
      ...prev,
      [sessionId]: newTimer
    }));
  };

  // Bulk delete functions
  const toggleSessionSelection = (sessionId) => {
    const newSelected = new Set(selectedSessions);
    if (newSelected.has(sessionId)) {
      newSelected.delete(sessionId);
    } else {
      newSelected.add(sessionId);
    }
    setSelectedSessions(newSelected);
  };

  const selectAllSessions = () => {
    const currentPageIds = paginatedSessions.map(s => s.id);
    setSelectedSessions(new Set([...selectedSessions, ...currentPageIds]));
  };

  const clearAllSelections = () => {
    setSelectedSessions(new Set());
  };

  const handleBulkDelete = async () => {
    if (selectedSessions.size === 0) {
      toast.error('Ingen treningsøkter er valgt for sletting');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const deletePromises = Array.from(selectedSessions).map(sessionId =>
        axios.delete(`${API_BASE}/api/training/sessions/${sessionId}`, { headers })
      );

      await Promise.all(deletePromises);
      
      toast.success(`${selectedSessions.size} treningsøkter slettet`);
      
      // Clear selections and reload sessions
      setSelectedSessions(new Set());
      setBulkDeleteMode(false);
      setShowBulkDeleteConfirm(false);
      await loadTrainingSessions();
      
    } catch (error) {
      console.error('Failed to delete sessions:', error);
      toast.error('Kunne ikke slette treningsøkter. Prøv igjen.');
    }
  };

  const generatePDFReport = (data) => {
    // This is a simplified version. In a real app, you'd use jsPDF or similar
    const reportContent = `
TRENINGSLOGG FOR VÅPENLISENSSØKNAD

Medlemsinformasjon:
Navn: ${data.user.full_name}
Medlemsnummer: ${data.user.member_number}

Treningsøkter (${data.total_sessions} økter):
${data.sessions.map((session, index) => `
${index + 1}. ${new Date(session.start_time).toLocaleDateString('no-NO')} - ${session.location_name}
   Aktivitet: ${session.activity || 'Pistoltrening'}
   Status: ${session.verified ? 'Godkjent' : 'Venter godkjenning'}
   ${session.notes ? `Notater: ${session.notes}` : ''}
`).join('')}

Generert: ${new Date().toLocaleDateString('no-NO')}
    `;
    
    // Create and download file
    const blob = new Blob([reportContent], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `treningslogg-${data.user.full_name.replace(/\s+/g, '_')}-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Helper function to merge additional documents into PDF
  const mergePDFWithDocuments = async (pdfBytes, selectedDocUrls) => {
    if (!selectedDocUrls || selectedDocUrls.length === 0) {
      return pdfBytes; // No documents to merge
    }

    const failedDocs = [];
    let mainPdf;
    try {
      const { PDFDocument } = await import('pdf-lib');
      
      // Load the main PDF (Police-PDF from backend)
      try {
        mainPdf = await PDFDocument.load(pdfBytes);
      } catch (loadErr) {
        console.error('❌ Could not load main Police PDF for merging:', loadErr);
        return pdfBytes; // Return original – at least it downloads
      }
      
      // Fetch and merge each selected document
      for (const docUrl of selectedDocUrls) {
        try {
          console.log(`📄 Processing document: ${docUrl}`);
          
          // Determine if we need to use the proxy endpoint
          // Use proxy for CloudFront/S3 URLs to bypass CORS restrictions
          const isCloudFrontOrS3 = docUrl.includes('cloudfront.net') || docUrl.includes('amazonaws.com');
          const isCloudinary = docUrl.includes('cloudinary.com');
          const isExternalUrl = docUrl.startsWith('http://') || docUrl.startsWith('https://');
          
          let fetchUrl = docUrl;
          const fetchOptions = {};
          
          if (isCloudFrontOrS3) {
            // Use backend proxy for CloudFront/S3 URLs to bypass CORS
            const API_BASE_LOCAL = process.env.REACT_APP_BACKEND_URL || '';
            fetchUrl = `${API_BASE_LOCAL}/api/documents/proxy?url=${encodeURIComponent(docUrl)}`;
            fetchOptions.headers = {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            };
            console.log(`📄 Using proxy for CloudFront URL: ${fetchUrl}`);
          } else if (isCloudinary) {
            // Cloudinary URLs can be fetched directly (they have CORS enabled)
            // URL-encode the filename part to handle spaces and special characters
            const urlParts = docUrl.split('/');
            const filename = urlParts[urlParts.length - 1];
            const encodedFilename = encodeURIComponent(filename);
            fetchUrl = urlParts.slice(0, -1).join('/') + '/' + encodedFilename;
            console.log(`📄 Fetching Cloudinary document directly: ${fetchUrl}`);
          } else if (!isExternalUrl) {
            // Local file path – add Authorization header and ensure single /api prefix
            fetchOptions.headers = {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            };
          }
          
          const response = await fetch(fetchUrl, fetchOptions);
          
          if (!response.ok) {
            console.warn(`⚠️ Failed to fetch document: ${fetchUrl} (Status: ${response.status})`);
            failedDocs.push(docUrl.split('/').pop() || 'vedlegg');
            continue;
          }
          
          const docBytes = await response.arrayBuffer();
          const contentType = response.headers.get('content-type');
          
          // Check if it's a PDF or image
          if (contentType && contentType.includes('pdf')) {
            // Merge PDF pages
            const docPdf = await PDFDocument.load(docBytes);
            const copiedPages = await mainPdf.copyPages(docPdf, docPdf.getPageIndices());
            copiedPages.forEach(page => mainPdf.addPage(page));
            console.log(`✅ Merged PDF document: ${docUrl}`);
          } else if (contentType && contentType.startsWith('image/')) {
            // Convert image to PDF page
            const page = mainPdf.addPage();
            const { width, height } = page.getSize();
            
            let image;
            try {
              if (contentType.includes('png')) {
                image = await mainPdf.embedPng(docBytes);
              } else if (contentType.includes('jpeg') || contentType.includes('jpg')) {
                image = await mainPdf.embedJpg(docBytes);
              } else {
                // HEIC, HEIF, WebP, SVG, etc. – pdf-lib cannot embed these
                console.warn(`⚠️ Unsupported image type for PDF: ${contentType}`);
                mainPdf.removePage(mainPdf.getPageCount() - 1); // Remove the blank page we just added
                failedDocs.push(docUrl.split('/').pop() || 'vedlegg');
                continue;
              }
            } catch (embedErr) {
              console.error(`❌ Could not embed image ${docUrl}:`, embedErr);
              mainPdf.removePage(mainPdf.getPageCount() - 1); // Remove the blank page we just added
              failedDocs.push(docUrl.split('/').pop() || 'vedlegg');
              continue;
            }
            
            // Scale image to fit page while maintaining aspect ratio
            const imageAspect = image.width / image.height;
            const pageAspect = width / height;
            
            let drawWidth, drawHeight;
            if (imageAspect > pageAspect) {
              // Image is wider - fit to width
              drawWidth = width * 0.9; // 90% of page width for margins
              drawHeight = drawWidth / imageAspect;
            } else {
              // Image is taller - fit to height
              drawHeight = height * 0.9; // 90% of page height for margins
              drawWidth = drawHeight * imageAspect;
            }
            
            // Center the image on the page
            const x = (width - drawWidth) / 2;
            const y = (height - drawHeight) / 2;
            
            page.drawImage(image, {
              x,
              y,
              width: drawWidth,
              height: drawHeight,
            });
            
            console.log(`✅ Merged image document: ${docUrl}`);
          } else {
            console.warn(`⚠️ Unsupported document type: ${contentType}`);
            failedDocs.push(docUrl.split('/').pop() || 'vedlegg');
          }
        } catch (docError) {
          console.error(`❌ Error merging document ${docUrl}:`, docError);
          failedDocs.push(docUrl.split('/').pop() || 'vedlegg');
          // Continue with other documents even if one fails
        }
      }
      
      // Notify user if any documents failed
      if (failedDocs.length > 0) {
        toast.error(
          `Kunne ikke flette ${failedDocs.length} av ${selectedDocUrls.length} vedlegg ` +
          `(støttet format er PDF, JPG og PNG). PDF lastes ned uten disse.`,
          { duration: 6000 }
        );
      }
      
      // Try to save the merged PDF – if even this fails, fall back to original
      try {
        return await mainPdf.save();
      } catch (saveErr) {
        console.error('❌ Could not save merged PDF, returning original:', saveErr);
        return pdfBytes;
      }
    } catch (error) {
      console.error('❌ Error in PDF merge:', error);
      toast.error('Kunne ikke legge til dokumenter i PDF. Laster ned hovedrapport.');
      return pdfBytes; // Return original PDF if merge fails
    }
  };

  // PDF download function (moved here to access filteredSessions)
  const downloadStandardPDF = async () => {
    setExportLoading(true);
    try {
      // Use filteredSessions that already has all filters applied
      // Then filter to only verified sessions for PDF
      const verifiedFilteredSessions = filteredSessions.filter(session => session.verified === true);
      
      if (verifiedFilteredSessions.length === 0) {
        toast.error('Ingen verifiserte økter funnet for valgte filtre');
        setExportLoading(false);
        return;
      }
      
      // Get selected organization info from filter
      let selectedClubName = user?.organization?.name || 'Klubb ikke tilgjengelig';
      let selectedOrgName = 'NSF - Norges Sportsskytterforbund'; // Default full name
      let selectedOrgShort = 'NSF'; // Default short name
      
      if (user?.organization?.member_organizations && user.organization.member_organizations[organizationFilter]) {
        const selectedOrg = user.organization.member_organizations[organizationFilter];
        selectedClubName = selectedOrg.clubName || selectedClubName;
        selectedOrgName = selectedOrg.organization; // Full name like "DSSN - Dynamisk Sportsskyting Norge"
        // Extract short name (before dash)
        selectedOrgShort = selectedOrg.organization.split(' - ')[0] || 'NSF';
      }
      
      // Import jsPDF dynamically
      const { default: jsPDF } = await import('jspdf');
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Header - følger din mal NØYAKTIG
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text(`TRENINGSLOGG - ${selectedOrgShort} (Trening)`, 105, 20, { align: 'center' });

      // Medlemsinformasjon (venstre side)
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Medlem:', 20, 40);
      pdf.setFont('helvetica', 'normal');
      pdf.text(user?.full_name || 'Navn ikke tilgjengelig', 40, 40);
      
      pdf.setFont('helvetica', 'bold');
      pdf.text('Fødselsdato:', 20, 50);
      pdf.setFont('helvetica', 'normal');
      pdf.text(user?.birth_number || 'Ikke oppgitt', 50, 50);
      
      pdf.setFont('helvetica', 'bold');
      pdf.text('Medlemsorganisasjon:', 20, 60);
      pdf.setFont('helvetica', 'normal');
      pdf.text(selectedOrgName, 70, 60); // Use full organization name
      
      pdf.setFont('helvetica', 'bold');
      pdf.text('Skytterlag/skytterklubb:', 20, 70);
      pdf.setFont('helvetica', 'normal');
      pdf.text(selectedClubName, 75, 70);

      // Logo placeholder (høyre side) - hvis logo finnes
      if (user?.organization?.logo_url) {
        // Legg til logo hvis tilgjengelig
        pdf.text(user.organization.name, 150, 40);
      } else {
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text(user?.organization?.name?.toUpperCase() || 'PISTOLKLUBB', 150, 40);
      }

      // KRAVSTATUS seksjon - beregn treninger innen siste 6 måneder
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      
      // Filter sessions within last 6 months for NSF requirement
      const sessionsLast6Months = verifiedFilteredSessions.filter(session => {
        const sessionDate = new Date(session.start_time);
        return sessionDate >= sixMonthsAgo;
      });
      
      const trainingsCount = sessionsLast6Months.length;
      const isRequirementMet = trainingsCount >= 10;
      
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0); // Svart tekst for "KRAVSTATUS:"
      pdf.text('KRAVSTATUS:', 20, 85);
      
      // Sett farge basert på om kravet er oppfylt
      if (isRequirementMet) {
        pdf.setTextColor(0, 128, 0); // Grønn tekst når oppfylt
      } else {
        pdf.setTextColor(220, 53, 69); // Rød tekst når ikke oppfylt
      }
      
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Standard våpensøknad: ${trainingsCount}/10 treninger innen 6 mnd (${isRequirementMet ? 'OPPFYLT' : 'IKKE OPPFYLT'})`, 20, 95);

      // Table header - NØYAKTIG som din mal med GUL bakgrunn
      let yPosition = 115;
      pdf.setFillColor(255, 215, 0); // Gul bakgrunn som i din mal
      pdf.rect(20, yPosition - 5, 170, 10, 'F');
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.text('Dato', 25, yPosition);
      pdf.text('Aktivitet', 55, yPosition);
      if (isDssnActive) {
        pdf.text('Stevne-nr', 95, yPosition);
      }
      pdf.text('Bane/sted', isDssnActive ? 115 : 95, yPosition);
      pdf.text('Verifisert av', isDssnActive ? 155 : 135, yPosition);
      
      yPosition += 15;
      
      // Table rows - NØYAKTIG som din mal med alternerende rader
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(0, 0, 0);
      
      verifiedFilteredSessions.forEach((session, index) => {
        if (yPosition > 245) { // New page if needed, mer plass til signatur
          pdf.addPage();
          yPosition = 30;
        }
        
        // Alternating row colors NØYAKTIG som i din mal
        if (index % 2 === 1) { // Annenhver rad grå
          pdf.setFillColor(248, 248, 248);
          pdf.rect(20, yPosition - 5, 170, 10, 'F');
        }
        
        pdf.text(new Date(session.start_time).toLocaleDateString('no-NO'), 25, yPosition);
        pdf.text((session.activity || 'Trening').substring(0, 18), 55, yPosition);
        if (isDssnActive) {
          pdf.text((session.competition_number || '-').substring(0, 8), 95, yPosition);
        }
        pdf.text((session.location_name || 'Ukjent').substring(0, 18), isDssnActive ? 115 : 95, yPosition);
        pdf.text((session.verified_by || 'Ikke verifisert').substring(0, 18), isDssnActive ? 155 : 135, yPosition);
        
        yPosition += 8;
      });

      // SIGNATUR OG STEMPEL seksjon - NØYAKTIG som din mal
      yPosition += 20;
      if (yPosition > 240) {
        pdf.addPage();
        yPosition = 30;
      }
      
      // Signatur seksjon ramme
      pdf.setDrawColor(0, 0, 0);
      pdf.rect(20, yPosition, 170, 40);
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.text('SIGNATUR OG STEMPEL', 25, yPosition + 10);
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.text('Styremedlem/skyteleders signatur:', 25, yPosition + 20);
      pdf.line(85, yPosition + 22, 160, yPosition + 22); // Signaturlinje
      
      pdf.text('Dato:', 25, yPosition + 30);
      pdf.line(40, yPosition + 32, 80, yPosition + 32); // Dato linje
      
      pdf.text('Signatur søker:', 25, yPosition + 37);
      pdf.line(65, yPosition + 39, 120, yPosition + 39); // Søker signatur linje
      
      // STEMPEL område - kun liten lys versjon for manuell stempling
      pdf.setTextColor(200, 200, 200);
      pdf.setFontSize(8);
      pdf.text('STEMPEL', 145, yPosition + 30);

      // Footer NØYAKTIG som din mal
      const pageCount = pdf.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        
        // Generert tid og klubb info (nederst høyre)
        const now = new Date();
        const genTime = `Generert: ${now.toLocaleDateString('no-NO')} ${now.toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' })}`;
        pdf.text(genTime, 190, 285, { align: 'right' });
        pdf.text(`${user?.organization?.name || 'Pistolklubb'} - www.aktivlogg.no`, 190, 290, { align: 'right' });
      }

      // Get PDF as bytes for potential merging
      const pdfBytes = pdf.output('arraybuffer');
      
      // Merge with selected documents if any
      const finalPdfBytes = await mergePDFWithDocuments(pdfBytes, selectedDocuments);
      
      // Create blob and download
      const blob = new Blob([finalPdfBytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const today = new Date().toISOString().split('T')[0];
      const orgName = (user?.organization?.name || 'Treningslogg').replace(/[^a-zA-Z0-9æøå]/g, '-');
      a.download = `treningslogg-${orgName}-${today}.pdf`;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      const docMessage = selectedDocuments.length > 0 
        ? ` (med ${selectedDocuments.length} vedlagt${selectedDocuments.length > 1 ? 'e' : ''} dokument${selectedDocuments.length > 1 ? 'er' : ''})`
        : '';
      toast.success(`PDF-treningslogg lastet ned${docMessage}!`);
      
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error('Kunne ikke generere PDF. Prøv igjen senere.');
    } finally {
      setExportLoading(false);
    }
  };

  const downloadPolicePDF = async () => {
    setExportLoading(true);
    
    try {
      console.log('🚀 Starting backend police PDF generation...');
      
      // Send filter parameters to backend so it can generate filtered PDF
      const filterParams = {};
      
      // Add active filters as parameters
      if (search) filterParams.search = search;
      if (dateFilter !== 'alle') filterParams.dateFilter = dateFilter;
      if (selectedBranches.size > 0) filterParams.selectedBranches = Array.from(selectedBranches); // Multi-select branches
      if (locationFilter) filterParams.locationFilter = locationFilter;
      if (rangeOfficerFilter) filterParams.rangeOfficerFilter = rangeOfficerFilter;
      if (filter !== 'all') filterParams.statusFilter = filter;
      if (activityTypes.size > 0) filterParams.activityTypes = Array.from(activityTypes);
      
      // Add branchFilter for member organization in PDF (use first selected branch if multiple)
      if (selectedBranches.size > 0) {
        const branchesArray = Array.from(selectedBranches);
        filterParams.branchFilter = branchesArray[0]; // Use first selected branch for member organization
        console.log('📋 Using branch for member organization:', filterParams.branchFilter);
      }
      
      // Add selected organization info for PDF header
      if (user?.organization?.member_organizations && user.organization.member_organizations[organizationFilter]) {
        const selectedOrg = user.organization.member_organizations[organizationFilter];
        filterParams.clubName = selectedOrg.clubName;
        filterParams.organizationName = selectedOrg.organization;
      }
      
      console.log('📊 Sending filter parameters to backend:', filterParams);
      
      // MULTI-CLUB: include cross-club sessions if enabled and user opted in
      if (pdfMultiClubEnabled && pdfMemberships.length > 1 && includeAllClubs) {
        filterParams.include_all_clubs = true;
      }
      
      const response = await axios.post(`${API_BASE}/api/pdf/generate-police-pdf`, filterParams, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        responseType: 'blob' // Important for PDF download
      });
      
      console.log('✅ Backend PDF generated successfully');
      
      // Get PDF bytes from blob
      const pdfBytes = await response.data.arrayBuffer();
      
      // Merge with selected documents if any
      const finalPdfBytes = await mergePDFWithDocuments(pdfBytes, selectedDocuments);
      
      // Create blob and download
      const finalBlob = new Blob([finalPdfBytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(finalBlob);
      const link = document.createElement('a');
      link.href = url;
      
      // Create filename with current user and date
      const today = new Date().toISOString().split('T')[0];
      const userName = user?.full_name?.replace(/\s+/g, '_') || 'medlem';
      link.download = `aktivitetslogg_vapensoknad_${userName}_${today}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('🎉 Police PDF downloaded successfully');
      const docMessage = selectedDocuments.length > 0 
        ? ` (med ${selectedDocuments.length} vedlagt${selectedDocuments.length > 1 ? 'e' : ''} dokument${selectedDocuments.length > 1 ? 'er' : ''})`
        : '';
      toast.success(`Politi-godkjent aktivitetslogg lastet ned${docMessage}!`);
      
    } catch (error) {
      console.error('❌ Error generating backend police PDF:', error);
      
      if (error.response?.status === 401) {
        toast.error('Du må være logget inn for å laste ned PDF');
      } else {
        toast.error(`Kunne ikke generere PDF: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      console.log('🏁 Backend PDF generation process completed');
      setExportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  // ADVANCED FILTERING LOGIC
  const filteredSessions = sessions.filter(session => {
    // Search filter
    if (search && !session.activity?.toLowerCase().includes(search.toLowerCase()) &&
        !session.location_name?.toLowerCase().includes(search.toLowerCase()) &&
        !session.range_officer_name?.toLowerCase().includes(search.toLowerCase())) {
      return false;
    }
    
    // Status filter
    if (filter === 'verified' && !session.verified) return false;
    if (filter === 'pending' && session.verified) return false;
    
    // Date range filter
    if (!isDateInRange(session.date || session.start_time, dateFilter)) {
      return false;
    }
    
    // Activity type filter (checkbox selection)
    if (activityTypes.size > 0 && !activityTypes.has(session.activity)) {
      return false;
    }
    
    // Location filter
    if (locationFilter && !session.location_name?.toLowerCase().includes(locationFilter.toLowerCase())) {
      return false;
    }
    
    // Range officer filter
    if (rangeOfficerFilter && !session.range_officer_name?.toLowerCase().includes(rangeOfficerFilter.toLowerCase())) {
      return false;
    }
    
    // Branch filter (multi-select)
    if (selectedBranches.size > 0) {
      const sessionBranch = getBranchFromLocation(session);
      if (!sessionBranch || !selectedBranches.has(sessionBranch.key)) {
        return false;
      }
    }
    
    // Skytedagbok filter
    if (diaryFilter !== 'alle') {
      const hasData = hasDiaryData(session);
      if (diaryFilter === 'med_dagbok' && !hasData) return false;
      if (diaryFilter === 'uten_dagbok' && hasData) return false;
    }
    
    // Role-based filtering: All users should only see their own sessions in main LOGG page
    // Admin > Logg shows all org sessions, but this is the personal LOGG view
    if (!isSuperAdmin) {
      // Members, skyteledere, and org_admin should only see their own sessions in main LOGG
      // MULTI-CLUB: also accept sessions from linked accounts (other clubs)
      const linkedUserIds = memberClubMemberships.map(m => m.user_id);
      const allowedUserIds = new Set([user.id, ...linkedUserIds]);
      if (!allowedUserIds.has(session.user_id)) {
        return false;
      }
    }
    
    // MULTI-CLUB: club filter (multi-select checkboxes)
    if (diaryFilters.club_ids && diaryFilters.club_ids.length > 0) {
      if (!diaryFilters.club_ids.includes(session.organization_id)) {
        return false;
      }
    }
    
    return true;
  }).sort((a, b) => {
    // Apply sorting
    let aValue, bValue;
    
    switch (sortColumn) {
      case 'start_time':
        aValue = new Date(a.start_time || a.date);
        bValue = new Date(b.start_time || b.date);
        break;
      case 'activity':
        aValue = a.activity || '';
        bValue = b.activity || '';
        break;
      case 'location_name':
        aValue = a.location_name || '';
        bValue = b.location_name || '';
        break;
      case 'verified_by':
        aValue = a.verified_by || a.range_officer_name || '';
        bValue = b.verified_by || b.range_officer_name || '';
        break;
      case 'verified':
        aValue = a.verified ? 1 : 0;
        bValue = b.verified ? 1 : 0;
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredSessions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSessions = filteredSessions.slice(startIndex, endIndex);
  const visibleSessions = tableExpanded
    ? paginatedSessions
    : paginatedSessions.slice(0, initialVisibleRows);

  const clearFilters = () => {
    setSearch('');
    setFilter('all');
    setDateFilter('alle');
    setActivityTypes(new Set());
    setLocationFilter('');
    setRangeOfficerFilter('');
    setSelectedBranches(new Set()); // Clear all selected branches
    setOrganizationFilter(0); // Reset to first organization
    setDiaryFilter('alle');
    setDiaryFilters({ weapon_id: '', ammo_id: '', tag: '', year: '', club_id: '' });
    setCurrentPage(1); // Reset to first page when clearing filters
  };
  
  const toggleActivityType = (activity) => {
    const newSet = new Set(activityTypes);
    if (newSet.has(activity)) {
      newSet.delete(activity);
    } else {
      newSet.add(activity);
    }
    setActivityTypes(newSet);
  };

  // Toggle branch selection (multi-select)
  const toggleBranch = (branch) => {
    const newSet = new Set(selectedBranches);
    if (newSet.has(branch)) {
      newSet.delete(branch);
    } else {
      newSet.add(branch);
    }
    setSelectedBranches(newSet);
  };

  const canSeeAllUsers = isSuperAdmin || isOrgAdmin;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">
            {canSeeAllUsers ? t('trainingLog.title', 'Komplett Treningslogg') : t('trainingLog.title', 'Min Treningslogg')}
          </h1>
          <p className="text-gray-400 mt-1">
            {canSeeAllUsers ? t('trainingLog.subtitle_all', 'Se treningslogger for alle medlemmer') : t('trainingLog.subtitle_own', 'Se treningslogger for dine økter')}
          </p>
        </div>
        
        <div className="flex space-x-3 mt-4 sm:mt-0">
          <button
            id="main-pdf-button"
            onClick={() => {
              console.log("🚀 PDF MODAL BUTTON CLICKED - should show modal");
              setShowPdfOptions(true);
            }}
            disabled={exportLoading}
            className="inline-flex items-center px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50"
          >
            <Download className="h-5 w-5 mr-2" />
            {exportLoading ? 'Genererer...' : 'Last ned PDF'}
          </button>
          
          <button
            onClick={() => setShowManualRegistration(true)}
            className="inline-flex items-center px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors"
          >
            <Plus className="h-5 w-5 mr-2" />
            Registrer Trening
          </button>
        </div>
      </div>
      
      {/* Moved bulk delete section to after table - see below */}

      {/* ADVANCED FILTERING SECTION - Uniform collapsible design for all screen sizes */}
      <div className="bg-gray-800 rounded-lg border border-gray-700">
        {/* Universal: Compact filter toggle for all screen sizes */}
        <div className="block">
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="w-full p-3 flex items-center justify-between text-white hover:bg-gray-700 transition-colors"
          >
            <span className="font-medium text-lg">Filtrer treningslogger</span>
            <div className="flex items-center space-x-2">
              {/* Clear filters button - inline on desktop, separate on mobile */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  clearFilters();
                }}
                className="text-red-400 hover:text-red-300 text-sm font-medium px-2 py-1 rounded"
              >
                Nullstill
              </button>
              {showMobileFilters ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </div>
          </button>
        </div>
        
        {/* Filter Content - Collapsible for all screen sizes */}
        <div className={`${showMobileFilters ? 'block' : 'hidden'} p-4`}>
          {/* Basic Search */}
          <div className="mb-4">
            <input
              type="text"
              placeholder={t('trainingLog.searchPlaceholder', 'Søk i aktivitet, sted, eller skyteleder...')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-yellow-500 focus:outline-none"
            />
          </div>

          {/* Skytedagbok-filtre (våpen / ammo / tag / år) */}
          <div className="mb-4 p-3 rounded-lg" style={{ background: '#0b1220', border: '1px solid #1f2937' }}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-extrabold tracking-widest uppercase" style={{ color: '#FFD700' }}>
                Skytedagbok-filter
                {activeFilterCount > 0 && (
                  <span className="ml-2 px-2 py-0.5 rounded-full text-[10px]" style={{ background: '#FFD700', color: '#030712' }}>
                    {activeFilterCount} aktiv{activeFilterCount > 1 ? 'e' : ''}
                  </span>
                )}
              </span>
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={clearDiaryFilters}
                  className="text-[10px] text-gray-400 hover:text-white underline"
                  data-testid="clear-diary-filters"
                >
                  Nullstill
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <select
                value={diaryFilters.weapon_id}
                onChange={(e) => setDiaryFilters((p) => ({ ...p, weapon_id: e.target.value }))}
                className="bg-gray-700 text-white text-sm rounded-md px-2 py-2 border border-gray-600 focus:border-yellow-500 focus:outline-none"
                data-testid="diary-filter-weapon"
              >
                <option value="">Alle våpen</option>
                {diaryWeapons.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}{w.caliber ? ` (${w.caliber})` : ''}
                  </option>
                ))}
              </select>
              <select
                value={diaryFilters.ammo_id}
                onChange={(e) => setDiaryFilters((p) => ({ ...p, ammo_id: e.target.value }))}
                className="bg-gray-700 text-white text-sm rounded-md px-2 py-2 border border-gray-600 focus:border-yellow-500 focus:outline-none"
                data-testid="diary-filter-ammo"
              >
                <option value="">All ammunisjon</option>
                {diaryAmmo.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}{a.batch_number ? ` (#${a.batch_number})` : ''}
                  </option>
                ))}
              </select>
              <select
                value={diaryFilters.tag}
                onChange={(e) => setDiaryFilters((p) => ({ ...p, tag: e.target.value }))}
                className="bg-gray-700 text-white text-sm rounded-md px-2 py-2 border border-gray-600 focus:border-yellow-500 focus:outline-none"
                data-testid="diary-filter-tag"
              >
                <option value="">Alle tagger</option>
                {['Trening', 'Stevne', 'Utendørs', 'Vind', 'God dag'].map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
                {diaryTags.map((t) => (
                  <option key={t.id} value={t.name}>{t.name}</option>
                ))}
              </select>
              <select
                value={diaryFilters.year}
                onChange={(e) => setDiaryFilters((p) => ({ ...p, year: e.target.value }))}
                className="bg-gray-700 text-white text-sm rounded-md px-2 py-2 border border-gray-600 focus:border-yellow-500 focus:outline-none"
                data-testid="diary-filter-year"
              >
                <option value="">Alle år</option>
                {(() => {
                  const yrs = [];
                  const now = new Date().getFullYear();
                  for (let y = now; y >= now - 5; y--) yrs.push(y);
                  return yrs.map((y) => <option key={y} value={y}>{y}</option>);
                })()}
              </select>
              {/* MULTI-CLUB: Klubb-filter som avhukingsbokser - vises kun for medlemmer i flere klubber */}
              {multiClubEnabled && memberClubMemberships.length > 1 && (
                <div className="col-span-full mt-2 p-3 bg-gray-800/40 border border-yellow-500/30 rounded-lg" data-testid="diary-filter-clubs-container">
                  <div className="text-xs font-bold uppercase tracking-wider text-yellow-400 mb-2">
                    Mine klubber
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {/* "Alle klubber" checkbox */}
                    <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-700/50 px-2 py-1 rounded">
                      <input
                        type="checkbox"
                        checked={diaryFilters.club_ids.length === 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setDiaryFilters((p) => ({ ...p, club_ids: [] }));
                          }
                        }}
                        className="h-4 w-4 text-yellow-500 focus:ring-yellow-500 bg-gray-700 border-gray-600 rounded"
                        data-testid="diary-filter-club-all"
                      />
                      <span className="text-sm text-white font-medium">Alle klubber</span>
                    </label>
                    {memberClubMemberships.map((m) => {
                      const checked = diaryFilters.club_ids.includes(m.organization_id);
                      return (
                        <label
                          key={m.organization_id}
                          className="flex items-center gap-2 cursor-pointer hover:bg-gray-700/50 px-2 py-1 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              setDiaryFilters((p) => {
                                let ids = [...p.club_ids];
                                if (e.target.checked) {
                                  if (!ids.includes(m.organization_id)) ids.push(m.organization_id);
                                } else {
                                  ids = ids.filter((id) => id !== m.organization_id);
                                }
                                return { ...p, club_ids: ids };
                              });
                            }}
                            className="h-4 w-4 text-yellow-500 focus:ring-yellow-500 bg-gray-700 border-gray-600 rounded"
                            data-testid={`diary-filter-club-${m.organization_id}`}
                          />
                          <span className="text-sm text-white truncate" title={m.organization_name}>
                            {m.organization_name}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Advanced Filters - Always show when expanded */}
          {showMobileFilters && (
          <div className="space-y-6">
            {/* Date Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">Tidsperiode</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  { value: 'alle', label: 'Alle' },
                  { value: '6months', label: 'Siste 6 måneder' },
                  { value: '1year', label: 'Siste år' },
                  { value: '2years', label: 'Siste 2 år' }
                ].map(option => (
                  <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="dateFilter"
                      value={option.value}
                      checked={dateFilter === option.value}
                      onChange={(e) => setDateFilter(e.target.value)}
                      className="text-yellow-500 focus:ring-yellow-500"
                    />
                    <span className="text-sm text-gray-300">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Branch Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">Skyttergren</label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {/* Alle option - selects/deselects all */}
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedBranches.size === 0}
                    onChange={() => setSelectedBranches(new Set())}
                    className="text-yellow-500 focus:ring-yellow-500 rounded"
                  />
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white bg-gray-600">
                    Alle grener
                  </span>
                </label>
                
                {/* Individual branches */}
                {[
                  { value: 'nsf', label: 'NSF', color: 'bg-blue-500' },
                  { value: 'dfs', label: 'DFS', color: 'bg-purple-500' },
                  { value: 'dssn', label: 'DSSN', color: 'bg-green-500' },
                  { value: 'sws', label: 'SWS', color: 'bg-yellow-600' },
                  { value: 'nsu', label: 'NSU', color: 'bg-gray-500' },
                  { value: 'nrof', label: 'NROF', color: 'bg-indigo-500' },
                  { value: 'nmsf', label: 'NMSF', color: 'bg-pink-500' },
                  { value: 'njff', label: 'NJFF', color: 'bg-emerald-500' },
                  { value: 'nbif', label: 'NBIF', color: 'bg-cyan-500' },
                  { value: 'nbsf', label: 'NBSF', color: 'bg-red-500' },
                  { value: 'nssf', label: 'NSSF', color: 'bg-teal-500' }
                ].map(option => (
                  <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedBranches.has(option.value)}
                      onChange={() => toggleBranch(option.value)}
                      className="text-yellow-500 focus:ring-yellow-500 rounded"
                    />
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white ${option.color}`}>
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
              {selectedBranches.size > 0 && (
                <p className="text-xs text-gray-400 mt-2">
                  {selectedBranches.size} gren{selectedBranches.size > 1 ? 'er' : ''} valgt
                </p>
              )}
            </div>
            
            {/* Member Organizations Filter */}
            {user?.organization?.member_organizations && user.organization.member_organizations.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">Medlemsorganisasjon</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {user.organization.member_organizations.map((org, index) => (
                  <label key={index} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="organizationFilter"
                      value={index}
                      checked={organizationFilter === index}
                      onChange={(e) => setOrganizationFilter(parseInt(e.target.value))}
                      className="text-yellow-500 focus:ring-yellow-500"
                    />
                    <span className="text-sm text-gray-300 flex flex-col">
                      <span className="font-medium">{org.clubName || 'Ukjent klubb'}</span>
                      <span className="text-xs text-gray-400">{org.organization}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            )}
            
            {/* Skytedagbok Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">Skytedagbok</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {[
                  { value: 'alle', label: 'Alle', icon: '📋' },
                  { value: 'med_dagbok', label: 'Med dagbok', icon: '📝' },
                  { value: 'uten_dagbok', label: 'Uten dagbok', icon: '📄' }
                ].map(option => (
                  <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="diaryFilter"
                      value={option.value}
                      checked={diaryFilter === option.value}
                      onChange={(e) => setDiaryFilter(e.target.value)}
                      className="text-yellow-500 focus:ring-yellow-500"
                    />
                    <span className="text-sm text-gray-300 flex items-center">
                      <span className="mr-1">{option.icon}</span>
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Activity Types Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">Aktivitetstyper</label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-32 overflow-y-auto">
                {uniqueActivityTypes.map(activity => (
                  <label key={activity} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={activityTypes.has(activity)}
                      onChange={() => toggleActivityType(activity)}
                      className="text-yellow-500 focus:ring-yellow-500"
                    />
                    <span className="text-sm text-gray-300 truncate">{activity}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Other Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Sted/Bane</label>
                <input
                  type="text"
                  placeholder="Filtrer på sted..."
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-yellow-500 focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Skyteleder</label>
                <input
                  type="text"
                  placeholder="Filtrer på skyteleder..."
                  value={rangeOfficerFilter}
                  onChange={(e) => setRangeOfficerFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-yellow-500 focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-yellow-500 focus:outline-none"
                >
                  <option value="all">Alle</option>
                  <option value="verified">Kun godkjent</option>
                  <option value="pending">Kun ventende</option>
                </select>
              </div>
            </div>
          </div>
        )}
        
          {/* Results count */}
          <div className="mt-4 text-sm text-gray-400">
            Viser {filteredSessions.length} av {sessions.length} treningslogger
            {(search || dateFilter !== 'alle' || activityTypes.size > 0 || locationFilter || rangeOfficerFilter || filter !== 'all') && 
              <span className="text-yellow-400 font-medium"> (filtrerte)</span>
            }
          </div>
        </div>
      </div>

      {/* Old filters section removed - replaced with advanced filtering above */}

      {/* SKYTELEDER Organization Statistics - Only shown for admin/range officer */}
      {(isOrgAdmin || isRangeOfficer || isSuperAdmin) && (
        <div className="bg-gradient-to-r from-yellow-900/20 to-yellow-800/20 border border-yellow-600/30 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-yellow-400 mb-4 flex items-center">
            <User className="h-5 w-5 mr-2" />
            SKYTELEDER - Organisasjonsoversikt
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-gray-800/50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <User className="h-5 w-5 text-purple-400" />
                </div>
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-400">Totale medlemmer</p>
                  <p className="text-xl font-bold text-white">{orgStats.totalMembers}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <FileText className="h-5 w-5 text-blue-400" />
                </div>
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-400">Totale økter</p>
                  <p className="text-xl font-bold text-white">{orgStats.totalSessions}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                </div>
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-400">Godkjente</p>
                  <p className="text-xl font-bold text-white">{orgStats.verifiedSessions}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="p-2 bg-orange-500/20 rounded-lg">
                  <Clock className="h-5 w-5 text-orange-400" />
                </div>
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-400">Venter godkjenning</p>
                  <p className="text-xl font-bold text-white">{orgStats.pendingSessions}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-500/20 rounded-lg">
                  <FileText className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-400">Snitt per medlem</p>
                  <p className="text-xl font-bold text-white">
                    {orgStats.totalMembers > 0 ? Math.round(orgStats.totalSessions / orgStats.totalMembers) : 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reversed order on mobile: stats+heading rendered at bottom, list at top. Desktop keeps original order. */}
      <div className="flex flex-col-reverse gap-6 md:flex-col md:gap-6" data-testid="minlogg-reorder-wrap">
        {/* MIN LOGG heading + stats (BOTTOM on mobile, TOP on desktop) */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">Min logg</h3>
          </div>

          {/* Statistics - Mobile: 2x2 grid, Desktop: 4 columns */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center">
            <FileText className="hidden md:block h-5 w-5 text-blue-400 mr-2" />
            <div>
              <p className="text-sm text-gray-400">
                {(search || dateFilter !== 'alle' || activityTypes.size > 0 || locationFilter || rangeOfficerFilter || filter !== 'all') ? 'Filtrerte økter' : 'Totale økter'}
              </p>
              <p className="text-xl font-bold text-white">
                {(search || dateFilter !== 'alle' || activityTypes.size > 0 || locationFilter || rangeOfficerFilter || filter !== 'all') ? filteredSessions.length : sessions.length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center">
            <Calendar className="hidden md:block h-5 w-5 text-purple-400 mr-2" />
            <div>
              <p className="text-sm text-gray-400">Denne måneden</p>
              <p className="text-xl font-bold text-white">
                {(() => {
                  const now = new Date();
                  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
                  const sessionsToCount = (search || dateFilter !== 'alle' || activityTypes.size > 0 || locationFilter || rangeOfficerFilter || filter !== 'all') ? filteredSessions : sessions;
                  return sessionsToCount.filter(s => new Date(s.start_time) >= thisMonthStart).length;
                })()}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="hidden md:block h-5 w-5 text-green-400 mr-2" />
            <div>
              <p className="text-sm text-gray-400">Godkjente</p>
              <p className="text-xl font-bold text-white">
                {(search || dateFilter !== 'alle' || activityTypes.size > 0 || locationFilter || rangeOfficerFilter || filter !== 'all') ? 
                  filteredSessions.filter(s => s.verified).length : 
                  sessions.filter(s => s.verified).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center">
            <Clock className="hidden md:block h-5 w-5 text-orange-400 mr-2" />
            <div>
              <p className="text-sm text-gray-400">Venter godkjenning</p>
              <p className="text-xl font-bold text-white">
                {(search || dateFilter !== 'alle' || activityTypes.size > 0 || locationFilter || rangeOfficerFilter || filter !== 'all') ? 
                  filteredSessions.filter(s => !s.verified).length : 
                  sessions.filter(s => !s.verified).length}
              </p>
            </div>
          </div>
        </div>
        {/* END: Min logg heading + stats (space-y-4 wrapper) */}
      </div>
        </div>

      {/* Training Sessions List */}
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        {filteredSessions.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 mb-4">
              {search || filter !== 'all' 
                ? 'Ingen treningsøkter matcher søket/filteret'
                : 'Ingen treningsøkter registrert ennå'
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  {/* Checkbox column for bulk delete */}
                  {bulkDeleteMode && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={paginatedSessions.length > 0 && paginatedSessions.every(s => selectedSessions.has(s.id))}
                        onChange={(e) => {
                          if (e.target.checked) {
                            selectAllSessions();
                          } else {
                            // Clear selections for current page only
                            const newSelected = new Set(selectedSessions);
                            paginatedSessions.forEach(s => newSelected.delete(s.id));
                            setSelectedSessions(newSelected);
                          }
                        }}
                        className="text-yellow-500 focus:ring-yellow-500 rounded"
                      />
                    </th>
                  )}
                  
                  {/* Sortable Dato Column - Hide icon on mobile, reduce left margin */}
                  <th 
                    className="px-1 md:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-600 transition-colors"
                    onClick={() => handleColumnSort('start_time')}
                  >
                    <div className="flex items-center space-x-1">
                      <Calendar className="hidden md:block h-4 w-4 mr-2" />
                      <span>Dato</span>
                      {sortColumn === 'start_time' && (
                        <span className="text-yellow-400">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  
                  {canSeeAllUsers && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Medlem
                    </th>
                  )}
                  
                  {/* Sortable Lokasjon Column - Hidden on mobile */}
                  <th 
                    className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-600 transition-colors"
                    onClick={() => handleColumnSort('location_name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Lokasjon</span>
                      {sortColumn === 'location_name' && (
                        <span className="text-yellow-400">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  
                  {/* Sortable Aktivitet Column */}
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-600 transition-colors"
                    onClick={() => handleColumnSort('activity')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Aktivitet</span>
                      {sortColumn === 'activity' && (
                        <span className="text-yellow-400">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  
                  {/* Stevne-nr Column - Only show if DSSN is active */}
                  {isDssnActive && (
                    <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-24">
                      Stevne-nr
                    </th>
                  )}
                  
                  {/* Sortable Status Column - Narrow on mobile */}
                  <th 
                    className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-600 transition-colors w-20 md:w-auto"
                    onClick={() => handleColumnSort('verified')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortColumn === 'verified' && (
                        <span className="text-yellow-400">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  
                  {/* Sortable Godkjent av Column - Hidden on mobile */}
                  <th 
                    className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-600 transition-colors"
                    onClick={() => handleColumnSort('verified_by')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Godkjent av</span>
                      {sortColumn === 'verified_by' && (
                        <span className="text-yellow-400">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  
                  {/* Skytedagbok Column - Change to "Notater" on mobile, narrow width */}
                  <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-20 md:w-auto">
                    <span className="md:hidden">Notater</span>
                    <span className="hidden md:inline">Skytedagbok</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {visibleSessions.map((session) => (
                  <tr key={session.id} className="hover:bg-gray-700/50">
                    {/* Checkbox for bulk delete */}
                    {bulkDeleteMode && (
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedSessions.has(session.id)}
                          onChange={() => toggleSessionSelection(session.id)}
                          className="text-yellow-500 focus:ring-yellow-500 rounded"
                        />
                      </td>
                    )}
                    
                    {/* Dato Cell - Hide icon on mobile, reduce left margin */}
                    <td className="px-1 md:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex items-center space-x-3">
                          <Calendar className="hidden md:block h-4 w-4 text-gray-400" />
                          <div>
                            <div className="text-sm font-medium text-white">
                              {new Date(session.start_time).toLocaleDateString('no-NO')}
                            </div>
                            <div className="text-sm text-gray-400">
                              {new Date(session.start_time).toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    {canSeeAllUsers && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <User className="h-4 w-4 text-gray-400 mr-2 hidden md:block" />
                          <div>
                            <div className="text-sm font-medium text-white md:whitespace-nowrap whitespace-normal break-words leading-tight">
                              {session.user_name || 'Ukjent'}
                            </div>
                            {session.member_number && (
                              <div className="text-sm text-gray-400">
                                #{session.member_number}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    )}
                    
                    <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-white">
                          {session.location_name || 'Ukjent lokasjon'}
                        </span>
                      </div>
                    </td>
                    
                    <td className="px-4 md:px-6 py-4">
                      <div className="flex flex-col md:flex-row md:items-center md:space-x-2 space-y-1 md:space-y-0">
                        <span className="text-sm text-white">
                          {session.activity || session.training_type || 'Pistoltrening'}
                        </span>
                        {(() => {
                          const branch = getBranchFromLocation(session);
                          return branch ? (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${branch.color} ${branch.textColor} w-fit`}>
                              {branch.name}
                            </span>
                          ) : null;
                        })()}
                        {/* Show location on mobile - hide pin icon */}
                        <span className="md:hidden text-xs text-gray-400">
                          {session.location_name || 'Ukjent'}
                        </span>
                      </div>
                    </td>
                    
                    {/* Stevne-nr Cell - Only show if DSSN is active */}
                    {isDssnActive && (
                      <td className="px-2 md:px-4 py-4">
                        <input
                          type="text"
                          value={session.competition_number || ''}
                          onChange={(e) => handleCompetitionNumberChange(session.id, e.target.value)}
                          placeholder="-"
                          className="w-20 md:w-24 px-2 py-1 bg-gray-700 text-white text-sm rounded border border-gray-600 focus:border-yellow-500 focus:outline-none"
                          maxLength={50}
                        />
                      </td>
                    )}
                    
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 md:px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        session.verified
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-orange-500/20 text-orange-400'
                      }`}>
                        {session.verified ? (
                          <>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            <span className="hidden md:inline">Godkjent</span>
                            <span className="md:hidden">✓</span>
                          </>
                        ) : (
                          <>
                            <Clock className="h-3 w-3 mr-1" />
                            <span className="hidden md:inline">Venter</span>
                          </>
                        )}
                      </span>
                    </td>
                    
                    <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {session.verified_by || '-'}
                    </td>
                    
                    {/* Skytedagbok Cell - Only show for own sessions */}
                    <td className="px-2 md:px-6 py-4 whitespace-nowrap">
                      {session.user_id === user?.id && (
                        <button
                          onClick={() => openDiaryModal(session)}
                          data-testid={`open-diary-btn-${session.id}`}
                          className={`inline-flex items-center px-2 md:px-3 py-1 md:py-2 rounded-lg transition-colors text-xs md:text-sm font-medium ${
                            hasDiaryData(session)
                              ? 'bg-green-600 text-white hover:bg-green-500 border border-green-500'
                              : 'bg-gray-600 text-gray-300 hover:bg-yellow-500 hover:text-gray-900 border border-gray-500'
                          }`}
                          title={hasDiaryData(session) ? "Skytedagbok med data - klikk for å redigere" : "Tom skytedagbok - klikk for å legge til"}
                        >
                          <Edit className="h-3 md:h-4 w-3 md:w-4 mr-1" />
                          <span className="hidden sm:inline">Skytedagbok</span>
                        </button>
                      )}
                      {session.user_id !== user?.id && (
                        <span className="text-gray-500 text-sm">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Vis mer / Vis færre - Mobile & Tablet only */}
            {paginatedSessions.length > initialVisibleRows && screenSize !== 'desktop' && (
              <button
                type="button"
                onClick={() => setTableExpanded((v) => !v)}
                data-testid="training-log-expand-btn"
                className="w-full py-3 bg-gray-800/70 hover:bg-gray-700/70 text-gray-300 hover:text-white text-sm font-medium flex items-center justify-center gap-2 transition-colors border-t border-gray-700"
                aria-expanded={tableExpanded}
              >
                {tableExpanded ? (
                  <>
                    Vis færre
                    <ChevronUp className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    Vis {paginatedSessions.length - initialVisibleRows} flere
                    <ChevronDown className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
          </div>
        )}
        
        {/* Pagination and Results Info */}
        {filteredSessions.length > 0 && (
          <div className="flex items-center justify-between bg-gray-800 px-6 py-3 border-t border-gray-700">
            {totalPages > 1 && (
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-md text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Forrige
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-md text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Neste
                </button>
              </div>
            )}
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-400">
                  Viser <span className="font-medium">{startIndex + 1}</span> til{' '}
                  <span className="font-medium">{Math.min(endIndex, filteredSessions.length)}</span> av{' '}
                  <span className="font-medium">{filteredSessions.length}</span> resultater
                </p>
              </div>
              {totalPages > 1 && (
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-600 bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Forrige</span>
                      ←
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      if (totalPages <= 7 || page <= 3 || page >= totalPages - 2 || Math.abs(page - currentPage) <= 1) {
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              page === currentPage
                                ? 'z-10 bg-yellow-600 border-yellow-600 text-black'
                                : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === 4 && currentPage > 4) {
                        return <span key={page} className="px-4 py-2 text-gray-400">...</span>;
                      } else if (page === totalPages - 3 && currentPage < totalPages - 3) {
                        return <span key={page} className="px-4 py-2 text-gray-400">...</span>;
                      }
                      return null;
                    })}
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-600 bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Neste</span>
                      →
                    </button>
                  </nav>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {/* END: flex-col-reverse wrapper (Min logg heading+stats and training list) */}
      </div>

      {/* Bulk Delete Section - Only for Skyteledere and Super Admins - Moved to bottom */}
      {(isOrgAdmin || isRangeOfficer || isSuperAdmin) && (
        <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-3 mt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <h3 className="text-gray-300 font-medium text-sm">Administrer oppføringer</h3>
              {!bulkDeleteMode ? (
                <button
                  onClick={() => setBulkDeleteMode(true)}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-gray-300 text-xs rounded transition-colors"
                >
                  Velg for sletting
                </button>
              ) : (
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-400">
                    {selectedSessions.size} valgt
                  </span>
                  <button
                    onClick={selectAllSessions}
                    disabled={paginatedSessions.length === 0}
                    className="text-blue-400 hover:text-blue-300 text-xs disabled:text-gray-500"
                  >
                    Velg alle på siden
                  </button>
                  <button
                    onClick={clearAllSelections}
                    disabled={selectedSessions.size === 0}
                    className="text-gray-400 hover:text-gray-300 text-xs disabled:text-gray-500"
                  >
                    Tøm valg
                  </button>
                  {selectedSessions.size > 0 && (
                    <button
                      onClick={() => setShowBulkDeleteConfirm(true)}
                      className="px-2 py-1 bg-orange-600 hover:bg-orange-700 text-white text-xs rounded transition-colors"
                    >
                      Slett ({selectedSessions.size})
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setBulkDeleteMode(false);
                      setSelectedSessions(new Set());
                    }}
                    className="px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white text-xs rounded transition-colors"
                  >
                    Ferdig
                  </button>
                </div>
              )}
            </div>
          </div>
          {bulkDeleteMode && (
            <p className="mt-1 text-xs text-gray-400">
              Kryss av oppføringer og klikk &ldquo;Slett&rdquo; for å fjerne flere samtidig.
            </p>
          )}
        </div>
      )}

      {/* Mobile Bookmark Banner - Bottom of page */}
      <MobileBookmarkBanner />

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-red-400 mb-4">Bekreft sletting</h3>
            <p className="text-gray-300 mb-6">
              Er du sikker på at du vil slette {selectedSessions.size} treningsoppføringer? 
              Denne handlingen kan ikke angres.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowBulkDeleteConfirm(false)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
              >
                Slett {selectedSessions.size} oppføringer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Notice for Members */}
      {!canSeeAllUsers && sessions.filter(s => s.verified).length >= 10 && (
        <div className="bg-green-600/20 border border-green-600/50 rounded-lg p-4">
          <h3 className="text-green-400 font-medium mb-2">PDF Export Tilgjengelig!</h3>
          <p className="text-green-300 text-sm mb-3">
            Du har {sessions.filter(s => s.verified).length} godkjente treningsøkter. 
            Du kan nå eksportere en politi-godkjent treningslogg for våpenlisenssøknader.
          </p>
          <button
            onClick={() => {
              console.log("🟢 Green notice PDF button clicked");
              setShowPdfOptions(true);
            }}
            disabled={exportLoading}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-500 transition-colors text-sm"
          >
            <Download className="h-4 w-4 mr-2" />
            {exportLoading ? 'Eksporterer...' : 'Last ned PDF'}
          </button>
        </div>
      )}

      {/* PDF Options Modal */}
      {showPdfOptions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-2 sm:p-4 overflow-hidden">
          <div className="bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-3xl mt-2 sm:my-8 max-h-[calc(100vh-1rem)] sm:max-h-[90vh] overflow-y-auto overscroll-contain relative">
            {/* Close button - top right */}
            <button
              onClick={() => {
                setShowPdfOptions(false);
                setSelectedDocuments([]);
              }}
              className="absolute top-2 right-2 sm:top-3 sm:right-3 p-1.5 sm:p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-full transition-colors z-10"
              aria-label="Lukk"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <h3 className="text-lg sm:text-xl font-medium text-white mb-4 sm:mb-6 pr-8">Velg PDF-format for eksport</h3>
            {console.log("🔍 PDF Options Modal is rendering, showPdfOptions:", showPdfOptions)}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {/* Police PDF Option - Default */}
              <div className="relative">
                <button
                  onClick={() => {
                    console.log("🚔 Police PDF option clicked");
                    setShowPdfOptions(false);
                    downloadPolicePDF();
                  }}
                  className="w-full p-3 sm:p-4 bg-yellow-600/20 border-2 border-yellow-500 rounded-lg hover:bg-yellow-600/30 transition-colors relative"
                >
                  <div className="absolute top-2 right-2 bg-yellow-500 text-black text-xs px-2 py-1 rounded font-bold">
                    ANBEFALT
                  </div>
                  
                  {/* Thumbnail placeholder for police form */}
                  <div className="w-full h-24 sm:h-32 bg-white rounded-lg mb-2 sm:mb-3 border flex items-center justify-center">
                    <div className="text-center text-gray-600 text-xs">
                      <div className="font-bold text-sm">POLITIET</div>
                      <div className="mt-1">Aktivitetslogg for våpensøknad</div>
                      <div className="mt-2 text-xs hidden sm:block">
                        <div>□ Organisert trening □ Klubbstevne</div>
                        <div>Sted: _____________</div>
                        <div>Dato: _____________</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-left">
                    <div className="text-white font-medium text-base sm:text-lg">Aktivitetslogg for våpensøknad</div>
                    <div className="text-yellow-300 text-xs sm:text-sm mt-1">Politiets offisielle skjema</div>
                    <div className="text-gray-300 text-xs sm:text-sm mt-2">
                      • Kun trening og klubbstevne aktiviteter
                      • Nøyaktig kopi av politiets PDF-skjema
                      • Kreves for våpenlisenssøknader
                      <span className="hidden sm:inline"> • 24 rader for registreringer</span>
                    </div>
                    <div
                      className="mt-3 flex items-start gap-2 p-2 bg-blue-900/30 border border-blue-500/40 rounded text-blue-200 text-xs"
                      data-testid="police-pdf-info-tooltip"
                    >
                      <Info className="h-4 w-4 flex-shrink-0 mt-0.5 text-blue-300" />
                      <span>
                        Alle skyterelaterte økter inkluderes som standard — bruk filtermenyen for å skreddersy hva du vil ha med.
                      </span>
                    </div>
                  </div>
                </button>
              </div>

              {/* Standard PDF Option */}
              <div>
                <button
                  onClick={() => {
                    console.log("📄 Standard PDF option clicked");
                    setShowPdfOptions(false);
                    downloadStandardPDF();
                  }}
                  className="w-full p-3 sm:p-4 bg-gray-700 border-2 border-gray-600 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  {/* Thumbnail placeholder for standard report */}
                  <div className="w-full h-24 sm:h-32 bg-white rounded-lg mb-2 sm:mb-3 border flex items-center justify-center">
                    <div className="text-center text-gray-600 text-xs">
                      <div className="font-bold">TRENINGSLOGG</div>
                      <div className="mt-1">FOR VÅPENLISENSSØKNAD</div>
                      <div className="mt-2">
                        <div>=================</div>
                        <div>Dato | Aktivitet | Bane</div>
                        <div>=================</div>
                        <div>01.01.24 | Trening | Bane 1</div>
                        <div>02.01.24 | Stevne | Bane 2</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-left">
                    <div className="text-white font-medium text-base sm:text-lg">Standard Treningslogg</div>
                    <div className="text-gray-400 text-xs sm:text-sm mt-1">Intern oversikt</div>
                    <div className="text-gray-300 text-xs sm:text-sm mt-2">
                      • Alle typer aktiviteter inkludert
                      • Detaljert oversikt med verifisering
                      <span className="hidden sm:inline"> • For intern bruk og dokumentasjon • Fleksibelt format</span>
                    </div>
                  </div>
                </button>
              </div>
            </div>
            
            {/* MULTI-CLUB: Cross-club PDF toggle - only shown for multi-club members */}
            {pdfMultiClubEnabled && pdfMemberships.length > 1 && (
              <div className="mt-6 border-t border-gray-700 pt-6">
                <div className="bg-yellow-500/5 border border-yellow-500/30 rounded-lg p-4">
                  <h4 className="text-base font-semibold text-yellow-400 mb-2 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Inkluder treninger fra dine andre klubber?
                  </h4>
                  <p className="text-xs text-gray-300 mb-3">
                    Du er medlem i {pdfMemberships.length} klubber. Politiet anbefaler å inkludere
                    alle treninger på tvers av klubber ved våpenlisenssøknad for å vise full aktivitet.
                  </p>
                  <div className="space-y-2">
                    <label className="flex items-start gap-3 p-3 bg-gray-700/50 hover:bg-gray-700 rounded cursor-pointer border border-transparent hover:border-yellow-500/40 transition-colors">
                      <input
                        type="radio"
                        checked={includeAllClubs}
                        onChange={() => setIncludeAllClubs(true)}
                        className="mt-1 text-yellow-500 focus:ring-yellow-500"
                        data-testid="pdf-include-all-clubs"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-white">
                          Alle mine klubber{' '}
                          <span className="text-xs bg-yellow-500 text-black px-2 py-0.5 rounded-full font-bold ml-1">ANBEFALT</span>
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {pdfMemberships.map((m) => m.organization_name).join(' · ')}
                        </div>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 p-3 bg-gray-700/50 hover:bg-gray-700 rounded cursor-pointer border border-transparent hover:border-yellow-500/40 transition-colors">
                      <input
                        type="radio"
                        checked={!includeAllClubs}
                        onChange={() => setIncludeAllClubs(false)}
                        className="mt-1 text-yellow-500 focus:ring-yellow-500"
                        data-testid="pdf-only-current-club"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-white">
                          Kun denne klubben
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {user?.organization?.name || 'Aktiv klubb'}
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            )}
            
            {/* Document Selection Section */}
            {(() => {
              // Collect all available documents from user profile
              const availableDocs = [];
              
              if (user?.startkort_url && typeof user.startkort_url === 'string') {
                // Check if it's already a full URL (Cloudinary)
                let fullUrl;
                let filename;
                
                if (user.startkort_url.startsWith('http://') || user.startkort_url.startsWith('https://')) {
                  // Already a full URL (e.g., Cloudinary) - use as-is
                  fullUrl = user.startkort_url;
                  filename = user.startkort_url.split('/').pop();
                } else if (user.startkort_url.startsWith('/api')) {
                  // Local file with /api path - use K8s ingress routing
                  fullUrl = `${window.location.origin}${user.startkort_url}`;
                  filename = user.startkort_url.split('/').pop();
                } else {
                  // Local file without /api path
                  fullUrl = `${API_BASE}${user.startkort_url}`;
                  filename = user.startkort_url.split('/').pop();
                }
                
                availableDocs.push({
                  url: fullUrl,
                  name: 'Startkort',
                  filename: filename
                });
              }
              
              if (user?.medlemskort_urls && user.medlemskort_urls.length > 0) {
                user.medlemskort_urls.forEach((url, idx) => {
                  // Guard: skip null/non-string entries to prevent .startsWith crash
                  if (!url || typeof url !== 'string') return;
                  // Check if it's already a full URL (Cloudinary)
                  let fullUrl;
                  let filename;
                  
                  if (url.startsWith('http://') || url.startsWith('https://')) {
                    // Already a full URL (e.g., Cloudinary) - use as-is
                    fullUrl = url;
                    filename = url.split('/').pop();
                  } else if (url.startsWith('/api')) {
                    // Local file with /api path - use K8s ingress routing
                    fullUrl = `${window.location.origin}${url}`;
                    filename = url.split('/').pop();
                  } else {
                    // Local file without /api path
                    fullUrl = `${API_BASE}${url}`;
                    filename = url.split('/').pop();
                  }
                  
                  availableDocs.push({
                    url: fullUrl,
                    name: `Medlemskort ${user.medlemskort_urls.length > 1 ? idx + 1 : ''}`.trim(),
                    filename: filename
                  });
                });
              }
              
              if (user?.weapon_cert_urls && user.weapon_cert_urls.length > 0) {
                user.weapon_cert_urls.forEach((url, idx) => {
                  // Guard: skip null/non-string entries to prevent .startsWith crash
                  if (!url || typeof url !== 'string') return;
                  // Check if it's already a full URL (Cloudinary)
                  let fullUrl;
                  let filename;
                  
                  if (url.startsWith('http://') || url.startsWith('https://')) {
                    // Already a full URL (e.g., Cloudinary) - use as-is
                    fullUrl = url;
                    filename = url.split('/').pop();
                  } else if (url.startsWith('/api')) {
                    // Local file with /api path - use K8s ingress routing
                    fullUrl = `${window.location.origin}${url}`;
                    filename = url.split('/').pop();
                  } else {
                    // Local file without /api path
                    fullUrl = `${API_BASE}${url}`;
                    filename = url.split('/').pop();
                  }
                  
                  availableDocs.push({
                    url: fullUrl,
                    name: `Våpenkort/Diplom ${user.weapon_cert_urls.length > 1 ? idx + 1 : ''}`.trim(),
                    filename: filename
                  });
                });
              }
              
              return availableDocs.length > 0 ? (
                <div className="mt-6 border-t border-gray-700 pt-6">
                  <h4 className="text-lg font-medium text-white mb-3 flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-blue-400" />
                    Legg til filer fra din PROFIL &gt; Dokumenter (velg hvilke filer du ønsker på slutten i PDF-en)
                  </h4>
                  
                  {/* Select All / Deselect All checkbox */}
                  <div className="mb-3 pb-3 border-b border-gray-600">
                    <label className="flex items-center space-x-3 p-2 hover:bg-gray-600/50 rounded cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedDocuments.length === availableDocs.length && availableDocs.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // Select all documents
                            setSelectedDocuments(availableDocs.map(doc => doc.url));
                          } else {
                            // Deselect all documents
                            setSelectedDocuments([]);
                          }
                        }}
                        className="h-4 w-4 text-yellow-500 focus:ring-yellow-500 bg-gray-700 border-gray-600 rounded"
                      />
                      <span className="text-sm font-medium text-yellow-400">
                        {selectedDocuments.length === availableDocs.length && availableDocs.length > 0 
                          ? 'Fjern alle' 
                          : 'Legg til alle'}
                      </span>
                      <span className="text-xs text-gray-500">({availableDocs.length} dokumenter tilgjengelig)</span>
                    </label>
                  </div>
                  
                  <div className="bg-gray-700/50 rounded-lg p-4 max-h-48 overflow-y-auto">
                    <div className="space-y-2">
                      {availableDocs.map((doc, index) => {
                        const isSelected = selectedDocuments.includes(doc.url);
                        // Clean up filename - remove user_id prefix if present
                        let displayFilename = doc.filename;
                        if (displayFilename.includes('_')) {
                          const parts = displayFilename.split('_');
                          if (parts.length > 1) {
                            displayFilename = parts.slice(1).join('_');
                          }
                        }
                        
                        return (
                          <label 
                            key={index}
                            className="flex items-center space-x-3 p-2 hover:bg-gray-600/50 rounded cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedDocuments([...selectedDocuments, doc.url]);
                                } else {
                                  setSelectedDocuments(selectedDocuments.filter(url => url !== doc.url));
                                }
                              }}
                              className="h-4 w-4 text-yellow-500 focus:ring-yellow-500 bg-gray-700 border-gray-600 rounded"
                            />
                            <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-white truncate">{doc.name}</div>
                              <div className="text-xs text-gray-400 truncate">{displayFilename}</div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  {selectedDocuments.length > 0 && (
                    <p className="text-sm text-blue-400 mt-2">
                      ✓ {selectedDocuments.length} dokument{selectedDocuments.length > 1 ? 'er' : ''} valgt
                    </p>
                  )}
                </div>
              ) : null;
            })()}
            
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-4 sm:mt-6 gap-3 sm:gap-0">
              <div className="text-gray-400 text-xs sm:text-sm max-w-xs sm:max-w-md">
                💡 Velg &ldquo;Aktivitetslogg for våpensøknad&rdquo; for offisielle henvendelser til politiet
              </div>
              <button
                onClick={() => {
                  console.log("❌ Modal close button clicked");
                  setShowPdfOptions(false);
                  setSelectedDocuments([]); // Clear selections when closing
                  console.log("✅ setShowPdfOptions(false) called");
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors w-full sm:w-auto"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Training Registration Modal */}
      {showManualRegistration && (
        <ManualTrainingRegistration
          isOpen={showManualRegistration}
          onClose={() => setShowManualRegistration(false)}
          onSuccess={() => {
            loadTrainingSessions(); // Refresh data after successful registration
          }}
        />
      )}

      {/* Discipline Boxes Section */}
      <DisciplineBoxes 
        organizationId={user?.organization_id} 
        user={user} 
        forceRefresh={disciplineRefresh}
      />

      {/* Training Diary Modal (Skytedagbok v2) */}
      <SkytedagbokModal
        session={selectedSession}
        isOpen={showDiaryModal}
        onClose={closeDiaryModal}
        onUpdate={handleDiaryUpdate}
      />
    </div>
  );
};

export default TrainingLog;